import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { marketplace, ThirdPartyIntegrationList } from '../../services/api';
import { useTranslation } from '../../contexts/TranslationContext';
import IntegrationCard from './components/IntegrationCard';
import IntegrationModal from './components/IntegrationModal';
import './Marketplace.css';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../../contexts/TourContext';
import { useTranslatedSteps } from '../stepsTour';
import { enqueueSnackbar } from 'notistack';
import VideoProvider from '../../components/VideoProvider';
import { videoUrls, videoTitles } from '../../utils/videoUrls';
import { notifyUserIntegrationsChanged } from './DidarAdvancedSettings';
import {
  DIDAR_SETTING_SNACKBAR_OPTIONS,
  isDidarMissingIntegrationFlag,
} from './didarSettingRoute';

export const MarketplaceContent: React.FC = () => {
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const didarMissingSnackbarShown = useRef(false);
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState<ThirdPartyIntegrationList[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, [page, searchTerm]);

  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  useEffect(() => {
    if (!isDidarMissingIntegrationFlag(searchParams) || didarMissingSnackbarShown.current) {
      return;
    }
    didarMissingSnackbarShown.current = true;
    enqueueSnackbar(
      t('marketplace.connections.didarMissingIntegration'),
      DIDAR_SETTING_SNACKBAR_OPTIONS,
    );
    const next = new URLSearchParams(searchParams);
    next.delete('didar_missing');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await marketplace.getIntegrations({
        page,
        size: 20,
        title_filter: searchTerm || undefined,
      });
      setIntegrations(response.data.items);
      setTotalPages(response.data.total_pages);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      setError(t('marketplace.error'));
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleAddIntegration = async (integrationId: number) => {
    try {
      await marketplace.addIntegration(integrationId);
      setShowModal(false);
      notifyUserIntegrationsChanged();
      // Show success message
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: t('marketplace.integrationAdded'), type: 'success' }
      }));
      enqueueSnackbar(t('marketplace.integrationAdded'), { variant: 'success', autoHideDuration: 2000, style: { padding: '10px', marginTop: '20px' } });
      // Navigate to Aida Settings > Connections > My Integrations (دوستان آیدا)
      navigate('/aida-settings?tab=3&subtab=1');
    } catch (error: any) {
      console.error('Failed to add integration:', error);
      const message = error.response?.data?.detail || t('marketplace.integrationAddError');
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message, type: 'error' }
      }));
    }
  };

  const handleViewDetails = async (integrationId: number) => {
    setSelectedIntegration(integrationId);
    setShowModal(true);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchIntegrations();
  };

  const handleSearchInputChange = (value: string) => {
    setSearchTerm(value);
    if (page !== 1) {
      setPage(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  if (initialLoading && loading && integrations.length === 0) {
    return (
      <div className="marketplace-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p className="marketplace-text-sm">{t('marketplace.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-container">
      <div className="marketplace-header first-step">
        <h2>{t('marketplace.title')}</h2>
        <p className="marketplace-text-sm">{t('marketplace.description')}</p>
      </div>

      <div className="marketplace-controls">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-bar">
            <input
              type="text"
              placeholder={t('marketplace.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="error-message">
          <p className="marketplace-text-sm">{error}</p>
          <button onClick={fetchIntegrations} className="retry-button">
            {t('marketplace.tryAgain')}
          </button>
        </div>
      )}

      <div className="integrations-grid">
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            onAddToAida={handleAddIntegration}
            onViewDetails={handleViewDetails}
          />
        ))}
      </div>

      {integrations.length === 0 && !loading && !error && (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h4>{t('marketplace.empty')}</h4>
          <p className="marketplace-text-sm">{t('marketplace.emptyDescription')}</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="pagination-button"
          >
            {t('marketplace.previous')}
          </button>
          
          <div className="pagination-info">
            <span className="marketplace-text-sm">{t('marketplace.page')} {page} {t('marketplace.of')} {totalPages}</span>
          </div>
          
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="pagination-button"
          >
            {t('marketplace.next')}
          </button>
        </div>
      )}

      {showModal && selectedIntegration && (
        <IntegrationModal
          integrationId={selectedIntegration}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onAdd={handleAddIntegration}
        />
      )}
    </div>
  );
};

const Marketplace: React.FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_marketplace}>
      <VideoProvider videoUrl={videoUrls.marketPlace} videoTitle={videoTitles.marketPlace}>
        <div style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <MarketplaceContent />
        </div>
      </VideoProvider>
    </TourProvider>
  );
};

export default Marketplace; 